import { useEffect, useState } from "react";
import type { ApprovalDTO } from "../../shared/types";
import { ApiError, get, post } from "../lib/api";
import { AgentAvatar } from "./AgentAvatar";
import { TextLink } from "./TextLink";
import { Badge, Button, ErrorText, inputCls, timeAgo } from "./ui";
import { AddressedTo, Decided } from "./Verdicts";

/**
 * An agent is not sure whether something belongs in a person's note, so it suggests it here. The card is addressed
 * to that person and nobody else: Add puts it in their note in these words, Edit lets them settle on their own
 * first, and No leaves the note alone. The platform writes it, never the agent. Anyone else, an admin included,
 * reads the card with its buttons drawn off.
 */
export function NoteCard({ approval, canDecide }: { approval: ApprovalDTO; canDecide: boolean }) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(approval.title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLocal(null);
    setEditing(false);
    setText(approval.title);
  }, [approval]);
  const a = local ?? approval;

  const decide = async (decision: "approve" | "reject") => {
    setBusy(true);
    setError(null);
    try {
      setLocal(await post<ApprovalDTO>(`/api/approvals/${a.id}/decide`, { decision, ...(decision === "approve" && editing ? { text: text.trim() } : {}) }));
      setEditing(false);
    } catch (err) {
      // Someone else decided it, or is deciding it right now: show where it actually stands instead of an error.
      const fresh = err instanceof ApiError && err.status === 409 ? await get<ApprovalDTO>(`/api/approvals/${a.id}`).catch(() => null) : null;
      if (fresh && fresh.status !== "pending") return setLocal(fresh);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card border-l-2 border-l-emerald-400 p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="emerald">Note</Badge>
        <span className="text-muted">
          <span className="font-medium text-fg">{a.agentName}</span> suggests this for your note
        </span>
      </div>
      {a.status === "pending" && editing ? (
        <textarea
          className={`${inputCls} mt-3`}
          rows={3}
          autoFocus
          maxLength={300}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="The note, in your words"
        />
      ) : (
        <p className="mt-2 text-reading leading-6 font-medium tracking-tight break-words">{a.title}</p>
      )}
      {a.reason && <p className="mt-2 text-sm leading-6 break-words whitespace-pre-wrap text-muted">{a.reason}</p>}
      {a.status === "withdrawn" ? (
        <Decided a={a} />
      ) : a.status !== "pending" ? (
        <p className="mt-4 border-t border-line pt-3 text-sm text-muted">
          <span className={a.status === "approved" ? "text-success" : "text-faint"}>{a.status === "approved" ? "✓" : "✕"}</span>{" "}
          <span className="font-medium text-fg">{a.status === "approved" ? "Added to your note" : "Not added"}</span> by {a.decidedByName ?? "someone"}{" "}
          {timeAgo(a.decidedAt)}
          {a.status === "approved" && (
            <>
              {" · "}
              <TextLink quiet to="/me">
                About me
              </TextLink>
            </>
          )}
          {a.error && <span className="mt-1.5 block text-danger">Could not add it: {a.error}</span>}
        </p>
      ) : canDecide ? (
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={busy || (editing && !text.trim())} onClick={() => decide("approve")}>
              Add
            </Button>
            {!editing && (
              <Button disabled={busy} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            <Button variant="danger" disabled={busy} onClick={() => decide("reject")}>
              No
            </Button>
          </div>
          <p className="text-xs text-faint">
            Everything in your note is in front of every agent that works for you. Change it any time on{" "}
            <TextLink quiet to="/me">
              About me
            </TextLink>
            .
          </p>
          <ErrorText error={error} />
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled>
              Add
            </Button>
            <Button disabled>Edit</Button>
            <Button variant="danger" disabled>
              No
            </Button>
          </div>
          <AddressedTo a={a} suffix=" — it is their note" />
        </div>
      )}
    </div>
  );
}
